/// <reference types="vite/client" />
import axios from 'axios';
import type { Voice, TTSParams, Config } from '../types/index';

// 请求去重缓存
const pendingRequests = new Map<string, Promise<unknown>>();

// 缓存配置
const CACHE_DURATION = {
  CONFIG: 24 * 60 * 60 * 1000, // 配置缓存24小时
  VOICES: 4 * 60 * 60 * 1000,  // 声音列表缓存4小时
};

// 缓存辅助函数
const saveToCache = (key: string, data: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.warn(`保存缓存失败 [${key}]:`, error);
  }
};

const getFromCache = (key: string, maxAge: number): unknown | null => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    if (now - timestamp < maxAge) {
      console.log(`✓ 使用缓存数据 [${key}]，年龄: ${Math.round((now - timestamp) / 1000)}秒`);
      return data;
    } else {
      console.log(`× 缓存已过期 [${key}]`);
      localStorage.removeItem(key);
      return null;
    }
  } catch (error) {
    console.warn(`读取缓存失败 [${key}]:`, error);
    return null;
  }
};

// 创建 axios 实例
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/', // 使用相对路径
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：添加 API Key
api.interceptors.request.use(
  (config) => {
    const apiKey = localStorage.getItem('tts_api_key');
    if (apiKey) {
      config.headers.Authorization = `Bearer ${apiKey}`;
      if (process.env.NODE_ENV === 'development') {
        console.log('API 请求认证:', {
          url: config.url,
          method: config.method,
          hasApiKey: !!apiKey,
          authHeader: config.headers.Authorization ? '已设置' : '未设置'
        });
      }
    } else {
      // API Key 是可选的，仅在开发环境输出调试信息
      if (process.env.NODE_ENV === 'development') {
        console.log('API 请求未设置认证密钥（可选）:', config.url);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器：处理错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
        console.error('API 认证失败:', {
            url: error.config?.url,
            status: error.response.status,
            statusText: error.response.statusText,
            hasApiKey: !!localStorage.getItem('tts_api_key')
        });
        error.message = 'API Key 无效或已过期，请检查认证设置';
    } else if (error.response?.status === 403) {
        console.error('API 访问被拒绝:', error.config?.url);
        error.message = '访问被拒绝，权限不足';
    }
    return Promise.reject(error);
  }
);

// API 服务类
export class TTSApiService {
  // 健康检查
  static async healthCheck(): Promise<unknown> {
    try {
      const response = await api.get('/api/v1/health');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // 获取配置信息
  static async getConfig(): Promise<Config> {
    const cacheKey = 'config';
    const storageKey = 'tts_config_cache';

    // 优化：检查localStorage缓存
    const cached = getFromCache(storageKey, CACHE_DURATION.CONFIG);
    if (cached) {
      return cached as Config;
    }

    // 如果已有请求在进行中，返回相同的 Promise
    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey) as Promise<Config>;
    }

    const requestPromise = this.makeConfigRequest();
    pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;

      // 保存到缓存
      saveToCache(storageKey, result);

      return result as Config;
    } finally {
      pendingRequests.delete(cacheKey);
    }
  }

  // 实际的配置请求方法
  private static async makeConfigRequest(): Promise<Config> {
    try {
      const response = await api.get('/api/v1/config');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // 获取声音列表
  static async getVoices(): Promise<Voice[]> {
    const cacheKey = 'voices-all';
    const storageKey = 'tts_voices_cache';

    // 优化：检查localStorage缓存
    const cached = getFromCache(storageKey, CACHE_DURATION.VOICES);
    if (cached) {
      return cached as Voice[];
    }

    // 如果已有请求在进行中，返回相同的 Promise
    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey) as Promise<Voice[]>;
    }

    const requestPromise = this.makeVoicesRequest();
    pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;

      // 保存到缓存
      saveToCache(storageKey, result);

      return result as Voice[];
    } finally {
      pendingRequests.delete(cacheKey);
    }
  }

  // 实际的声音列表请求方法
  private static async makeVoicesRequest(): Promise<Voice[]> {
    try {
      const response = await api.get('/api/v1/voices');

      // 优化：使用独立的方法进行数据转换，提高可维护性
      return response.data.map(TTSApiService.transformVoiceData);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // 优化：独立的数据转换方法，提高性能和可维护性
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static transformVoiceData(voice: any): Voice {
    return {
      id: voice.short_name || voice.name,
      name: voice.name,
      locale: voice.locale,
      gender: voice.gender,
      styles: voice.style_list || voice.styles || [],
      roles: voice.roles || [],
      sampleRate: voice.sample_rate_hertz ? parseInt(voice.sample_rate_hertz) : undefined,
      short_name: voice.short_name,
      local_name: voice.local_name,
      locale_name: voice.locale_name,
      display_name: voice.display_name,
      style_list: voice.style_list,
      sample_rate_hertz: voice.sample_rate_hertz,
    };
  }

  // 文本转语音
  static async synthesizeSpeech(params: TTSParams): Promise<Blob> {
    try {
      // 移除 api_key 参数，使用 Authorization 头认证
      const { api_key, ...cleanParams } = params as any;

      const response = await api.post('/api/v1/tts', cleanParams, {
        responseType: 'blob',
      });

      // 调试响应信息
      console.log('API 响应调试信息:', {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        dataType: response.data.constructor.name,
        dataSize: response.data.size,
        contentType: response.data.type,
      });

      // 验证响应数据
      if (!(response.data instanceof Blob)) {
        throw new Error('服务器返回的不是音频数据');
      }

      if (response.data.size === 0) {
        throw new Error('音频数据为空');
      }

      // 验证音频数据的头部字节
      const audioBlob = response.data;
      try {
        const headerBytes = await audioBlob.slice(0, 12).arrayBuffer();
        const header = new Uint8Array(headerBytes);
        console.log('音频文件头部字节:', Array.from(header).map(b => b.toString(16).padStart(2, '0')).join(' '));

        // 检查是否为有效的音频文件格式
        const isValidMp3 = header[0] === 0xFF && (header[1] & 0xE0) === 0xE0; // MP3 sync bytes
        const isValidWav = header[0] === 0x52 && header[1] === 0x49 && header[2] === 0x46 && header[3] === 0x46; // "RIFF"
        const isValidMpeg = header[0] === 0x49 && header[1] === 0x44 && header[2] === 0x33; // "ID3"

        console.log('音频格式验证:', {
          isMp3: isValidMp3,
          isWav: isValidWav,
          isMpeg: isValidMpeg,
          type: audioBlob.type
        });

        if (!isValidMp3 && !isValidWav && !isValidMpeg) {
          console.warn('警告: 音频文件格式可能不正确');
        }
      } catch (headerError) {
        console.warn('无法读取音频文件头部:', headerError);
      }

      return audioBlob;
    } catch (error) {
      console.error('API 请求失败:', error);
      throw this.handleError(error);
    }
  }

  // 文本转语音（历史记录重新生成）
  static async regenerateSpeech(historyItem: Record<string, unknown>): Promise<Blob> {
    try {
      const params = {
        text: historyItem.text as string,
        voice: historyItem.voice as string,
        style: (historyItem.style as string) || undefined,
        rate: historyItem.rate as string,
        pitch: historyItem.pitch as string,
      };
      const response = await api.post('/api/v1/tts', params, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // OpenAI 兼容接口
  static async openAISpeech(params: {
    model: string;
    input: string;
    voice: string;
    response_format?: string;
    speed?: number;
  }): Promise<Blob> {
    try {
      const response = await api.post('/v1/audio/speech', params, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // 处理错误
  private static handleError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.error || error.message;
      return new Error(message);
    }
    return error instanceof Error ? error : new Error('Unknown error');
  }
}

export default TTSApiService;
